/* ============================================================
   FORGE × FERAL FUNGI — motion + interactions
   Depends on (loaded before this): gsap, ScrollTrigger, Lenis (all optional-safe).
   Progressive enhancement: every behaviour degrades to plain content.
   ============================================================ */
(function () {
  "use strict";

  var REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var hasGSAP = typeof window.gsap !== "undefined";
  var hasST = hasGSAP && typeof window.ScrollTrigger !== "undefined";
  if (hasST) window.gsap.registerPlugin(window.ScrollTrigger);

  /* Smooth scroll (Lenis) was removed — it conflicted with the Palo Alto
     theme's own scroll handling. Native scroll is used; reveals run on
     IntersectionObserver and parallax on ScrollTrigger, both of which work
     with native scroll. */

  /* ---------------- Scroll reveals ----------------
     IntersectionObserver-driven (CSS transitions). Reliable for above-the-fold
     content — fires immediately for elements already in view, independent of
     GSAP/Lenis scroll sync. */
  function initReveals(root) {
    root = root || document;
    var solos = root.querySelectorAll("[data-ff-reveal]:not(.is-in)");
    var groups = root.querySelectorAll("[data-ff-reveal-stagger]:not(.is-in)");

    if (REDUCED || !("IntersectionObserver" in window)) {
      solos.forEach(function (el) { el.classList.add("is-in"); });
      groups.forEach(function (el) { el.classList.add("is-in"); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.01 });

    function reveal(el) { el.classList.add("is-in"); }
    function inView(el, pad) {
      var r = el.getBoundingClientRect();
      return r.top < (window.innerHeight || 0) + (pad || 0) && r.bottom > -(pad || 0);
    }

    var watched = [];
    solos.forEach(function (el) { if (inView(el)) reveal(el); else { io.observe(el); watched.push(el); } });
    groups.forEach(function (el) {
      var kids = el.children, i;
      for (i = 0; i < kids.length; i++) { kids[i].style.transitionDelay = (i * 0.08) + "s"; }
      if (inView(el)) reveal(el); else { io.observe(el); watched.push(el); }
    });

    // Scroll fallback: in contexts where IntersectionObserver doesn't fire,
    // reveal elements as they scroll into view. Self-removes when done.
    var ticking = false;
    function onScroll() {
      if (ticking) return; ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        watched = watched.filter(function (el) {
          if (el.classList.contains("is-in")) return false;
          if (inView(el, -40)) { reveal(el); io.unobserve(el); return false; }
          return true;
        });
        if (!watched.length) {
          window.removeEventListener("scroll", onScroll);
          window.removeEventListener("resize", onScroll);
        }
      });
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
  }

  /* ---------------- Theme cart drawer refresh ----------------
     Palo Alto theme exposes window.cart (CartDrawer). Opening the drawer
     toggle alone shows stale/empty HTML until getCart() re-fetches the
     api-cart-items section and rebuilds the drawer. */
  function refreshThemeCartAfterAdd() {
    if (window.cart && typeof window.cart.getCart === "function") {
      window.cart.getCart();
      return true;
    }
    return false;
  }

  function openCartDrawerFallback() {
    var drawer = document.querySelector("[data-cart-drawer-toggle], [data-action='open-drawer'], .js-drawer-open-cart");
    if (drawer) drawer.click();
    else document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  /* ---------------- Parallax on media ---------------- */
  function initParallax(root) {
    // Parallax is a subtle desktop effect; skipping it on phones and tablets saves a lot of processing.
    if (REDUCED || !hasST || window.matchMedia("(max-width: 989px)").matches) return;
    (root || document).querySelectorAll("[data-ff-parallax] img").forEach(function (img) {
      if (img.__ffPar) return; img.__ffPar = true;
      window.gsap.to(img, {
        yPercent: 8, ease: "none",
        scrollTrigger: { trigger: img.closest("[data-ff-parallax]"), start: "top bottom", end: "bottom top", scrub: true }
      });
    });
  }

  /* ---------------- Count-up stats ---------------- */
  function initCounters(root) {
    (root || document).querySelectorAll("[data-ff-count]:not(.counted)").forEach(function (el) {
      var target = parseFloat(el.getAttribute("data-ff-count"));
      var decimals = (el.getAttribute("data-ff-count").split(".")[1] || "").length;
      if (REDUCED || !("IntersectionObserver" in window)) { el.textContent = target.toFixed(decimals); el.classList.add("counted"); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          el.classList.add("counted"); io.disconnect();
          var obj = { v: 0 };
          if (hasGSAP) {
            window.gsap.to(obj, { v: target, duration: 1.4, ease: "power2.out",
              onUpdate: function () { el.textContent = obj.v.toFixed(decimals); } });
          } else { el.textContent = target.toFixed(decimals); }
        });
      }, { threshold: 0.6 });
      io.observe(el);
    });
  }

  /* ---------------- Accordion (FAQ / ingredients) ---------------- */
  function initAccordions(root) {
    (root || document).querySelectorAll("[data-ff-accordion]").forEach(function (acc) {
      if (acc.__ff) return; acc.__ff = true;
      var single = acc.hasAttribute("data-ff-accordion-single");
      var isIngredients = acc.hasAttribute("data-ff-ingredients-acc");
      var mediaRoot = isIngredients ? acc.closest(".ff-ingredients__grid") : null;
      var photo = mediaRoot
        ? (mediaRoot.querySelector(".ff-ingredients__photo") || mediaRoot.querySelector("[data-ff-ingredients-media] img") || mediaRoot.querySelector("img"))
        : null;

      function swapIngredientPhoto(item) {
        if (!photo || !item) return;
        var url = item.getAttribute("data-ff-ing-image");
        if (!url) return;
        var current = photo.getAttribute("data-ff-ing-current") || photo.getAttribute("src") || "";
        if (current === url) return;
        photo.classList.add("is-swapping");
        var next = new Image();
        next.onload = function () {
          photo.src = url;
          photo.removeAttribute("srcset");
          photo.removeAttribute("sizes");
          photo.setAttribute("data-ff-ing-current", url);
          if (item.getAttribute("data-ff-ing-alt")) {
            photo.alt = item.getAttribute("data-ff-ing-alt");
          }
          photo.classList.remove("is-swapping");
        };
        next.onerror = function () {
          photo.classList.remove("is-swapping");
        };
        next.src = url;
      }

      function closeItem(item) {
        if (!item) return;
        item.classList.remove("is-open");
        var trigger = item.querySelector("[data-ff-acc-trigger]");
        var panel = item.querySelector("[data-ff-acc-panel]");
        if (trigger) trigger.setAttribute("aria-expanded", "false");
        if (panel) panel.style.maxHeight = null;
      }

      acc.addEventListener("click", function (e) {
        var trigger = e.target.closest("[data-ff-acc-trigger]");
        if (!trigger || trigger.disabled) return;
        var item = trigger.closest("[data-ff-acc-item]");
        var panel = item.querySelector("[data-ff-acc-panel]");
        var wasOpen = item.classList.contains("is-open");

        if (single) {
          acc.querySelectorAll("[data-ff-acc-item].is-open").forEach(function (other) {
            if (other !== item) closeItem(other);
          });
        }

        var open = single ? !wasOpen : item.classList.toggle("is-open");
        if (single && wasOpen) open = false;
        if (single) item.classList.toggle("is-open", open);
        trigger.setAttribute("aria-expanded", open ? "true" : "false");
        if (panel) panel.style.maxHeight = open ? panel.scrollHeight + "px" : null;
        if (open && isIngredients) swapIngredientPhoto(item);
      });
    });
  }

  /* ---------------- Sticky add-to-cart reveal ---------------- */
  function initStickyATC(root) {
    var bar = (root || document).querySelector("[data-ff-sticky-atc]");
    var anchor = (root || document).querySelector("[data-ff-atc-anchor]") ||
                 document.querySelector("[data-ff-atc]");
    if (!bar || !anchor || bar.__ff) return; bar.__ff = true;
    if (!("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      // show once the main Add-to-Cart has scrolled above the viewport
      bar.classList.toggle("is-visible", !e.isIntersecting && e.boundingClientRect.top < 0);
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ---------------- Money formatting ---------------- */
  function fmtMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat) ||
              (window.Shopify && window.Shopify.money_format) || "${{amount}}";
    var amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  /* ---------------- PDP buy box: variant + subscribe + price + cart ---------------- */
  function initBuyBox(root) {
    (root || document).querySelectorAll("[data-ff-buybox]:not(.ff-ready)").forEach(function (box) {
      box.classList.add("ff-ready");
      var form = box.querySelector("[data-ff-pdp-form]");
      var variants = box.querySelectorAll("[data-ff-variant]");
      var planSelect = box.querySelector("[data-ff-selling-plan-select]");
      var planWrap = box.querySelector("[data-ff-plan-wrap]");
      var plansEl = box.querySelector("[data-ff-variant-plans]");
      var plansMap = {};
      if (plansEl) {
        try { plansMap = JSON.parse(plansEl.textContent); } catch (e) { plansMap = {}; }
      }
      var atc = box.querySelector("[data-ff-atc]");
      var atcPrice = box.querySelector("[data-ff-atc-price]");
      var onetimePriceEl = box.querySelector("[data-ff-onetime-price]");
      var notifyEnabled = !!(atc && atc.getAttribute("data-notification-popup"));
      var latestVariantId = "";
      var latestVariantTitle = "";
      if (atc && notifyEnabled) {
        var initInp = box.querySelector('[data-ff-variant] input[name="id"]:checked') ||
                      box.querySelector('[data-ff-variant] input[name="id"]');
        if (initInp) latestVariantId = String(initInp.value || "");
        var initVar = initInp ? initInp.closest("[data-ff-variant]") : null;
        var initName = initVar ? initVar.querySelector(".ff-size__name") : null;
        if (initName) latestVariantTitle = initName.textContent.trim();
      }

      function soldOutLabel() {
        var sold = (window.theme && window.theme.strings && window.theme.strings.sold_out) || "Sold Out";
        var notify = (window.theme && window.theme.strings && window.theme.strings.newsletter_product_availability) || "Notify Me When It’s Available";
        return notifyEnabled ? (sold + " - " + notify) : sold;
      }

      function syncSoldOutUi(v) {
        var soldOut = !!(v && v.classList.contains("is-soldout"));
        if (form) form.classList.toggle("variant--soldout", soldOut);
        if (!atc) return;

        var label = atc.querySelector("[data-ff-atc-label]");
        var arrow = atc.querySelector(".ff-btn__arrow");
        var stickyBtn = document.querySelector("[data-ff-sticky-add]");
      if (stickyBtn && !window.__ffStickyArrow) { var ffArrowEl = stickyBtn.querySelector(".ff-btn__arrow"); if (ffArrowEl) window.__ffStickyArrow = ffArrowEl.outerHTML; }

        if (soldOut) {
          atc.classList.toggle("ff-pdp-atc--notify", notifyEnabled);
          if (notifyEnabled) {
            atc.setAttribute("data-add-to-cart", "");
            atc.removeAttribute("disabled");
          } else {
            atc.removeAttribute("data-add-to-cart");
            atc.setAttribute("disabled", "disabled");
          }
          if (label) label.textContent = soldOutLabel();
          if (arrow) arrow.hidden = true;

          var inp = v.querySelector('input[name="id"]');
          var vid = inp ? String(inp.value || "") : "";
          var nameEl = v.querySelector(".ff-size__name");
          var vTitle = nameEl ? nameEl.textContent.trim() : "";
          var popupHtml = atc.getAttribute("data-notification-popup") || "";
          if (popupHtml && latestVariantId && vid && latestVariantId !== vid) {
            popupHtml = popupHtml.split(latestVariantId).join(vid);
          }
          if (popupHtml && latestVariantTitle && vTitle && latestVariantTitle !== vTitle) {
            popupHtml = popupHtml.split(latestVariantTitle).join(vTitle);
          }
          if (popupHtml) atc.setAttribute("data-notification-popup", popupHtml);
          if (vid) latestVariantId = vid;
          if (vTitle) latestVariantTitle = vTitle;

          if (stickyBtn) {
            stickyBtn.textContent = notifyEnabled ? soldOutLabel() : ((window.theme && window.theme.strings && window.theme.strings.sold_out) || "Sold Out");
            stickyBtn.disabled = !notifyEnabled;
            stickyBtn.classList.toggle("ff-sticky-atc__btn--notify", notifyEnabled);
          }
        } else {
          atc.classList.remove("ff-pdp-atc--notify");
          atc.removeAttribute("data-add-to-cart");
          atc.removeAttribute("disabled");
          if (label) {
            label.innerHTML = 'Add to cart · <span data-ff-atc-price></span>';
            atcPrice = atc.querySelector("[data-ff-atc-price]");
          }
          if (arrow) arrow.hidden = false;
          if (stickyBtn) {
            stickyBtn.innerHTML = 'Add to cart ' + (window.__ffStickyArrow || '<span class="ff-btn__arrow" aria-hidden="true">→</span>');
            stickyBtn.disabled = false;
            stickyBtn.classList.remove("ff-sticky-atc__btn--notify");
          }
        }
      }

      function setAtcPriceDisplay(once, sub, subscribed) {
        if (!atc) return;
        atcPrice = atc.querySelector("[data-ff-atc-price]");
        if (!atcPrice) return;
        var showStrike = !!(subscribed && sub && once && once > sub);
        if (showStrike) {
          atcPrice.innerHTML =
            '<span class="ff-pdp-atc__was">' + fmtMoney(once) + "</span>" +
            '<span class="ff-pdp-atc__now">' + fmtMoney(sub) + "</span>";
        } else {
          atcPrice.textContent = fmtMoney(subscribed && sub ? sub : once);
        }
      }

      function plansForVariantEl(v) {
        if (!v) return [];
        var inp = v.querySelector('input[name="id"]');
        var vid = inp ? inp.value : "";
        return plansMap[vid] || [];
      }

      function fillPlanSelect(v) {
        if (!planSelect) return;
        var plans = plansForVariantEl(v);
        planSelect.innerHTML = "";
        var def = 0;
        plans.forEach(function (p, i) { if (def === 0 && /\b30 days?\b/i.test(p.name)) def = i; });
        plans.forEach(function (p, i) {
          var opt = document.createElement("option");
          opt.value = String(p.id);
          opt.textContent = p.name;
          opt.setAttribute("data-price", String(p.price));
          if (i === def) opt.selected = true;
          planSelect.appendChild(opt);
        });
        if (plans[def] && v) {
          v.setAttribute("data-price-sub", String(plans[def].price));
          v.setAttribute("data-plan", String(plans[def].id));
        }
      }

      function syncPlanUi() {
        var subscribed = mode() === "subscribe";
        var v = selectedVariant();
        var plans = plansForVariantEl(v);
        var show = subscribed && plans.length > 0;
        if (planWrap) planWrap.hidden = !show;
        if (planSelect) {
          planSelect.disabled = !show;
          if (!show) planSelect.removeAttribute("name");
          else planSelect.setAttribute("name", "selling_plan");
        }
      }

      function selectedVariant() {
        var checked = box.querySelector('[data-ff-variant] input[name="id"]:checked');
        return checked ? checked.closest("[data-ff-variant]") : variants[0];
      }
      function mode() {
        var m = box.querySelector("[data-ff-purchase]:checked");
        return m ? m.value : "subscribe";
      }
      function update() {
        var v = selectedVariant();
        if (!v) return;
        var once = parseInt(v.getAttribute("data-price-once"), 10) || 0;
        var sub = parseInt(v.getAttribute("data-price-sub"), 10);
        var plan = v.getAttribute("data-plan") || "";
        if (planSelect && planSelect.options.length && mode() === "subscribe") {
          var opt = planSelect.options[planSelect.selectedIndex];
          if (opt) {
            plan = opt.value;
            sub = parseInt(opt.getAttribute("data-price"), 10) || sub;
            v.setAttribute("data-plan", plan);
            v.setAttribute("data-price-sub", String(sub));
          }
        }
        var subscribed = mode() === "subscribe" && plan;
        var price = subscribed && sub ? sub : once;
        syncSoldOutUi(v);
        if (!v.classList.contains("is-soldout")) {
          setAtcPriceDisplay(once, sub, subscribed);
        }
        if (onetimePriceEl) onetimePriceEl.textContent = fmtMoney(once);
        syncPlanUi();
        // selected-state classes (works without :has too)
        variants.forEach(function (el) { el.classList.toggle("is-selected", el === v); });
        box.querySelectorAll("[data-ff-purchase-opt]").forEach(function (opt) {
          var inp = opt.querySelector("[data-ff-purchase]");
          opt.classList.toggle("is-selected", inp && inp.checked);
        });
        // mirror to sticky bar
        var sName = document.querySelector("[data-ff-sticky-name]");
        var sPrice = document.querySelector("[data-ff-sticky-price]");
        var sSize = document.querySelector("[data-ff-sticky-size]");
        if (sPrice && !v.classList.contains("is-soldout")) sPrice.textContent = fmtMoney(price);
        var nameEl = box.querySelector(".ff-pdp-hero__title");
        if (sName && nameEl) sName.textContent = nameEl.textContent;
        var nm = v.querySelector(".ff-size__name");
        if (sSize && nm) sSize.textContent = nm.textContent;
      }

      var v0 = selectedVariant();
      if (v0) fillPlanSelect(v0);
      syncPlanUi();

      if (planSelect) {
        planSelect.addEventListener("change", function () {
          var v = selectedVariant();
          var opt = planSelect.options[planSelect.selectedIndex];
          if (v && opt) {
            v.setAttribute("data-plan", opt.value);
            v.setAttribute("data-price-sub", opt.getAttribute("data-price") || "");
          }
          update();
        });
      }

      box.addEventListener("change", function (e) {
        if (e.target.matches('input[name="id"]')) {
          var v = selectedVariant();
          if (v) fillPlanSelect(v);
        }
        if (e.target.matches('input[name="id"], [data-ff-purchase]')) update();
      });
      update();

      // AJAX add-to-cart (available variants only; sold-out uses theme notify popup)
      var openingNotify = false;
      function isNotifySoldOut() {
        return !!(form && form.classList.contains("variant--soldout") && notifyEnabled && atc);
      }
      function openNotifyPopup() {
        if (!atc || openingNotify) return;
        // Theme cart listener opens NotificationPopup on [data-add-to-cart] click
        if (!atc.hasAttribute("data-add-to-cart")) atc.setAttribute("data-add-to-cart", "");
        openingNotify = true;
        atc.click();
        setTimeout(function () { openingNotify = false; }, 100);
      }
      function addToCart() {
        if (!form || openingNotify) return;
        if (isNotifySoldOut()) {
          openNotifyPopup();
          return;
        }
        if (form.classList.contains("variant--soldout")) return;
        syncPlanUi();
        var fd = new FormData(form);
        fd.delete("ff_purchase");
        if (mode() !== "subscribe") fd.delete("selling_plan");
        if (!fd.has("quantity")) fd.set("quantity", "1");

        if (atc) atc.classList.add("is-loading");
        var addUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url)
          || (window.routes && window.routes.cart_add_url)
          || "/cart/add";
        fetch(addUrl + ".js", {
          method: "POST",
          headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: fd
        }).then(function (r) { return r.json(); }).then(function (data) {
          if (atc) atc.classList.remove("is-loading");
          if (data && data.status) return;
          if (!refreshThemeCartAfterAdd()) openCartDrawerFallback();
          document.dispatchEvent(new CustomEvent("ajaxProduct:added"));
        }).catch(function () { if (atc) atc.classList.remove("is-loading"); });
      }
      if (form) form.addEventListener("submit", function (e) {
        e.preventDefault();
        // Sold-out + notify: theme opens popup on the button click (preventDefault).
        // Only fall back if submit fired without that click path (e.g. Enter in a field).
        if (isNotifySoldOut()) {
          if (!openingNotify) openNotifyPopup();
          return;
        }
        if (form.classList.contains("variant--soldout")) return;
        addToCart();
      });
      // sticky bar add button proxies to the main form / notify popup
      document.querySelectorAll("[data-ff-sticky-add]").forEach(function (b) {
        if (b.__ffNotifyBound) return;
        b.__ffNotifyBound = true;
        b.addEventListener("click", function (e) {
          e.preventDefault();
          if (isNotifySoldOut()) {
            openNotifyPopup();
            return;
          }
          addToCart();
        });
      });
    });
  }

  /* ---------------- AJAX quick-add (cards) ---------------- */
  function initQuickAdd(root) {
    (root || document).querySelectorAll("[data-ff-quickadd]:not(.ff-ready)").forEach(function (btn) {
      btn.classList.add("ff-ready");
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        var id = btn.getAttribute("data-variant-id");
        if (!id) return;
        var plan = btn.getAttribute("data-selling-plan");
        var body = { items: [{ id: parseInt(id, 10), quantity: 1 }] };
        if (plan) body.items[0].selling_plan = parseInt(plan, 10);
        btn.classList.add("is-loading");
        fetch((window.theme && window.theme.routes ? window.theme.routes.cart_add_url : "/cart/add") + ".js", {
          method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify(body)
        }).then(function (r) { return r.json(); }).then(function (data) {
          btn.classList.remove("is-loading");
          if (data && data.status) return;
          btn.classList.add("is-done");
          if (!refreshThemeCartAfterAdd()) openCartDrawerFallback();
          setTimeout(function () { btn.classList.remove("is-done"); }, 1800);
        }).catch(function () { btn.classList.remove("is-loading"); });
      });
    });
  }

  /* ---------------- Announcement bar dismiss (mobile) ---------------- */
  function initDismiss(root) {
    (root || document).querySelectorAll("[data-ff-dismiss]:not(.ff-ready)").forEach(function (btn) {
      btn.classList.add("ff-ready");
      btn.addEventListener("click", function () {
        var t = btn.closest("[data-ff-dismissable]");
        if (t) t.style.display = "none";
      });
    });
  }

  /* ---------------- Autoplay background videos ----------------
     Native `autoplay muted playsinline` already plays muted videos when they
     scroll into view across modern browsers; we only nudge play() and honour
     reduced-motion (show the poster, don't autoplay). No off-screen pausing —
     it raced with native autoplay and left videos stuck paused. */
  function initHeroVideo(root) {
    (root || document).querySelectorAll("[data-ff-hero-video]").forEach(function (v) {
      if (v.__ffSetup) return; v.__ffSetup = true;
      if (REDUCED) { v.removeAttribute("autoplay"); try { v.pause(); } catch (e) {} return; }
      var inView = function () { var r = v.getBoundingClientRect(); return r.top < (window.innerHeight || 0) && r.bottom > 0; };
      var nudge = function () { var p = v.play(); if (p && p.catch) p.catch(function () {}); };
      // Chrome issues "pause" interventions on background videos mid-page even
      // when fully visible. Re-play whenever it's paused while in view; this is
      // seamless (no stutter) and keeps it looping. Off-screen pauses are left
      // alone (saves CPU), and the observer below restarts it on re-entry.
      // Lazy videos (data-ff-lazy) keep their file in data-src until they come near the screen, so they do not load with the page.
      var lazy = v.hasAttribute("data-ff-lazy");
      var loadSrc = function () {
        if (!lazy || v.__ffLoaded) return;
        v.__ffLoaded = true;
        v.querySelectorAll("source[data-src]").forEach(function (s) { s.src = s.getAttribute("data-src"); s.removeAttribute("data-src"); });
        v.load();
      };
      v.addEventListener("pause", function () { if (!REDUCED && inView()) nudge(); });
      if (!lazy || inView()) { loadSrc(); nudge(); }
      if ("IntersectionObserver" in window) {
        var io = new IntersectionObserver(function (es) {
          es.forEach(function (e) { if (e.isIntersecting) { loadSrc(); nudge(); } });
        }, { threshold: 0.01, rootMargin: lazy ? "400px 0px" : "0px" });
        io.observe(v);
      } else { loadSrc(); }
    });
  }

  /* ---------------- Boot ---------------- */
  function boot(root) {
    initHeroVideo(root);
    initReveals(root);
    initParallax(root);
    initCounters(root);
    initAccordions(root);
    initStickyATC(root);
    initBuyBox(root);
    initQuickAdd(root);
    initDismiss(root);
  }

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }
  ready(function () { boot(document); });

  // Re-init individual sections inside the Shopify theme editor
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();


/* FF cart: subscribe and save toggle per line (Oct 2026) */
(function () {
  if (window.__ffCartSub) return;
  window.__ffCartSub = true;
  function root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/"; }
  function refresh() {
    if (window.cart && typeof window.cart.getCart === "function") window.cart.getCart();
    else window.location.reload();
  }
  function setPlan(box, plan) {
    box.classList.add("is-loading");
    var body = { id: box.getAttribute("data-key"), quantity: parseInt(box.getAttribute("data-qty"), 10) || 1, selling_plan: plan ? parseInt(plan, 10) : null };
    fetch(root() + "cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }, body: JSON.stringify(body) })
      .then(function (r) { return r.json(); })
      .then(function () { refresh(); })
      .catch(function () { window.location.reload(); });
  }
  document.addEventListener("change", function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var box = t.closest("[data-ff-cart-sub]");
    if (!box) return;
    if (t.matches("[data-ff-sub-toggle]")) setPlan(box, t.checked ? box.getAttribute("data-default-plan") : null);
    else if (t.matches("[data-ff-sub-plan]")) setPlan(box, t.value);
  });
})();
